import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, when, reaction } from 'mobx';
import { shallowEqual } from 'shallow-equal-object';
import { applyDecorators } from "@app/Helpers/Decorator";

enum PopupTrigger {
    Click = 'click',
    Hover = 'hover',
    Manual = 'manual'
}

type PopupProps = {
    container: React.RefObject<HTMLElement>;
    onAppear?: (popupElement: HTMLElement) => Promise<boolean>;
    onDisappear?: () => void;
    children(popupElement: HTMLElement): JSX.Element | null;
    cssClass?: string;
    styles?: React.CSSProperties;
    trigger?: 'click' | 'hover' | 'manual';
    placement?: 'top' | 'left' | 'bottom' | 'right';
    targetId?: string;
};

type PlacementCoords = {
    x: number;
    y: number;
    position: 'top' | 'bottom' | 'left' | 'left-bottom' | 'left-top' | 'right' | 'right-bottom' | 'right-top';
};

class PopupPlacement {
    public static readonly top: string = 'top';
    public static readonly right: string = 'right';
    public static readonly bottom: string = 'bottom';
    public static readonly left: string = 'left';
}

@observer
export class Popup extends React.Component<PopupProps> {
    @observable private _popup: React.RefObject<HTMLDivElement> = React.createRef();
    @observable private _isVisible: boolean = false;
    @observable private _isFreezed: boolean = false;
    @observable.ref private _target: HTMLElement | null = null;
    @observable private _container: React.RefObject<HTMLElement> | null = null;
    private _timer: number | null = null;
    private _observer: MutationObserver | null = null;

    constructor(props: PopupProps) {
        super(props);
        applyDecorators(this);
        this._container = props.container;
        when(
            () => !!(this._container && this._container.current),
            () => {
                const { container, trigger } = this.props;
                const triggerType = trigger ?? PopupTrigger.Hover;
                if (container.current) {
                    container.current.addEventListener('scroll', this._documentScrollHandler);

                    if (triggerType === PopupTrigger.Hover) {
                        container.current.addEventListener('mouseover', this._onContainerMouseOver);
                        container.current.addEventListener('mouseleave', this._onContainerMouseOut);
                    } else if (triggerType === PopupTrigger.Click) {
                        container.current.addEventListener('click', this._onContainerClick);
                        document.addEventListener('mousedown', this._onContainerOutsideClick);
                    }
                }
            }
        );

        reaction(
            () => this._popup.current,
            () => {
                if (!!this._popup.current) {
                    this.startObserving();
                    this._updatePopupPosition();
                }
            }
        );
    }

    componentDidUpdate(prevProps: PopupProps) {
        if (!shallowEqual(prevProps, this.props)) {
            this._container = this.props.container;
        }
    }

    componentDidMount() {
        const { container } = this.props;
        if (container && container.current) container.current.addEventListener('scroll', this._documentScrollHandler);
        document.addEventListener('scroll', this._documentScrollHandler);
        window.addEventListener('resize', this._updatePopupPosition);
    }

    componentWillUnmount() {
        const { container, trigger } = this.props;
        if (container && container.current) {
            container.current.removeEventListener('scroll', this._documentScrollHandler);
            const triggerType = trigger ?? PopupTrigger.Hover;

            if (triggerType === PopupTrigger.Hover) {
                container.current.removeEventListener('mouseover', this._onContainerMouseOver);
                container.current.removeEventListener('mouseleave', this._onContainerMouseOut);
            } else if (triggerType === PopupTrigger.Click) {
                container.current.removeEventListener('click', this._onContainerClick);
                document.removeEventListener('mousedown', this._onContainerOutsideClick);
            }
        }
        document.removeEventListener('mouseout', this._documentMouseOutHandler);
        document.removeEventListener('scroll', this._documentScrollHandler);
        window.removeEventListener('resize', this._updatePopupPosition);
    }

    startObserving() {
        if (this._popup.current) {
            this._observer = new MutationObserver(() => {
                this._updateTarget();
                this._updatePopupPosition();
            });
            this._observer.observe(this._popup.current, {
                childList: true,
                subtree: true
            });
        }
    }

    stopObserving() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    @action.bound
    private _onContainerMouseOut() {
        this._hide();
    }

    @action.bound
    private _onContainerMouseOver(event: MouseEvent) {
        const currentTarget = event.target as HTMLElement;
        if (currentTarget !== this._target && !currentTarget.closest('.popup')) {
            this._hide();
            this._timer = window.setTimeout(this._checkTarget, 500, currentTarget);
        }
    }

    @action.bound
    private _onContainerClick(event: MouseEvent) {
        event.preventDefault();
        const target = event.target as HTMLElement;
        void this._checkTarget(target);
    }

    @action.bound
    private _onContainerOutsideClick(event: MouseEvent) {
        if (this._isVisible && !this._isFreezed && !this._popup.current?.contains(event.target as Node)) {
            this._hide();
        }
    }

    @action.bound
    public onHidePopup() {
        this._hide();
    }

    @action.bound
    public setFreezed(isFreezed: boolean) {
        this._isFreezed = isFreezed;
    }

    private _stopTimer() {
        if (this._timer) {
            window.clearTimeout(this._timer);
            this._timer = null;
        }
    }

    @action.bound
    private _hide() {
        this._isVisible = false;
        this._stopTimer();
        this._target = null;
        this.stopObserving();
        this.props.onDisappear?.();
    }

    @action.bound
    private async _checkTarget(sheduledTarget: HTMLElement) {
        if (this.props.onAppear) {
            const canShowPopup = await this.props.onAppear(sheduledTarget);
            if (canShowPopup) {
                this._isVisible = true;
                this._target = sheduledTarget;
            }
        }
    }

    private _updateTarget() {
        const { targetId } = this.props;
        if (!targetId) return;

        const target = document.getElementById(targetId);
        if (this._target !== target) {
            this._target = target;
        }
    }

    @action.bound
    private _documentMouseOutHandler() {
        this._hide();
    }

    @action.bound
    private _documentScrollHandler() {
        this._hide();
    }

    render() {
        if (!this._isVisible) return null;

        const { cssClass, styles, placement } = this.props;
        const defaultPosition = placement ?? PopupPlacement.top;
        const classNames = ['popup', `popup-${defaultPosition}`];
        if (cssClass) classNames.push(cssClass);

        return (
            <div ref={this._popup} className={classNames.join(' ')} style={styles}>
                {this._target && this.props.children(this._target)}
            </div>
        );
    }

    @action.bound
    private _updatePopupPosition() {
        if (!this._popup.current) return;
        if (!this._target) return;

        const targetRect = this._target.getBoundingClientRect();
        const popupRect = this._popup.current.getBoundingClientRect();

        const defaulPlacement = this.props.placement ?? PopupPlacement.top;
        const popupPlacementOrder = this._getPlacementOrder(defaulPlacement);
        const popup = this._popup.current;

        for (let i = 0; i < 4; i++) {
            const placement = popupPlacementOrder[i];
            const coords = this._getPlacementCoords(placement, targetRect, popupRect);

            if (coords) {
                this._setPopupPosition(popup, coords.x, coords.y, coords.position);
                return;
            }
        }

        this._setPopupPosition(popup);
    }

    private _getPlacementCoords(placement: string, targetRect: DOMRect, popupRect: DOMRect): PlacementCoords | null {
        if (!document.documentElement) {
            return null;
        }

        const docWidth = document.documentElement.clientWidth;
        const docHeight = document.documentElement.clientHeight;

        const coords: PlacementCoords = { x: 0, y: 0, position: 'top' };

        if (placement === PopupPlacement.top || placement === PopupPlacement.bottom) {
            const targetXCenter = targetRect.right - targetRect.width / 2;
            const popupHalfWidth = popupRect.width / 2;

            const fitwidthL = this._fit(targetXCenter, popupHalfWidth, window.scrollX);
            const fitwidthR = this._fit(targetXCenter, popupHalfWidth, window.scrollX + docWidth);

            if (!fitwidthL || !fitwidthR) return null;

            let y = 0;
            if (placement === PopupPlacement.top) {
                const fitHeight = this._fit(targetRect.top, popupRect.height, 0);
                if (fitHeight) {
                    y = targetRect.top - popupRect.height;
                    coords.position = 'top';
                }
            }

            if (placement === PopupPlacement.bottom) {
                const fitHeight = this._fit(targetRect.bottom, popupRect.height, window.scrollY + docHeight);
                if (fitHeight) {
                    y = targetRect.bottom;
                    coords.position = 'bottom';
                }
            }
            if (!y) return null;

            coords.x = targetXCenter - popupRect.width / 2;
            coords.y = y;
        }

        if (placement === PopupPlacement.right || placement === PopupPlacement.left) {
            const targetYCenter = targetRect.bottom - targetRect.height / 2;
            const popupHalfHeight = popupRect.height / 2;

            const fitHeightT = this._fit(targetYCenter, popupHalfHeight, 0);
            const fitHeightB = this._fit(targetYCenter, popupHalfHeight, window.scrollY + docHeight);

            let y = targetYCenter - popupHalfHeight;
            let x = 0;

            if (placement === PopupPlacement.right) {
                const fitWidth = this._fit(targetRect.right, popupRect.width, window.scrollX + docWidth);
                if (fitWidth) {
                    x = targetRect.right;
                    coords.position = 'right';
                }
            }

            if (placement === PopupPlacement.left) {
                const fitWidth = this._fit(targetRect.left, popupRect.width, window.scrollX);
                if (fitWidth) {
                    x = targetRect.left - popupRect.width;
                    coords.position = 'left';
                }
            }

            if (!fitHeightB) {
                y = targetYCenter - popupRect.height + 10;
                coords.position += '-bottom';
            }
            if (!fitHeightT) {
                y = targetYCenter - 10;
                coords.position += '-top';
            }

            if (!x) return null;

            coords.y = y;
            coords.x = x;
        }

        return coords.x !== 0 && coords.y !== 0 ? coords : null;
    }

    private _getPlacementOrder(placement: string): string[] {
        const defaultPopupPlacementOrder = [PopupPlacement.top, PopupPlacement.bottom, PopupPlacement.left, PopupPlacement.right];
        if (placement) {
            const index = defaultPopupPlacementOrder.findIndex(order => order === placement);
            defaultPopupPlacementOrder.splice(index, 1);
            defaultPopupPlacementOrder.unshift(placement);
        }

        return defaultPopupPlacementOrder;
    }

    private _fit(targetPoint: number, popupSide: number, containerPoint: number, indent: number = 0): boolean {
        return Math.abs(containerPoint - targetPoint) > popupSide + indent;
    }

    private _setPopupPosition(popup: HTMLDivElement | null, x: number = 0, y: number = 0, position?: string) {
        if (popup) {
            popup.style.left = `${x}px`;
            popup.style.top = `${y}px`;

            if (position) {
                const popupClass = Array.from(popup.classList).find(cls => cls.startsWith('popup-'));
                if (popupClass) {
                    popup.classList.remove(popupClass);
                }

                popup.classList.add(`popup-${position}`);
            }
        }
    }
}
